/**
 * PDP pack tilt + flip. Idle orbit is CSS on .daddl-pack-tilt__bob
 * (is-float). Mouse / drag / flip use a short RAF spring on the card.
 * is-live pauses the bob — it must not remove is-float or the orbit
 * restarts from 0%. No gyro.
 * data-embed: homepage rail. Desktop: hover flips, click opens the PDP.
 * Phone: tap the pack flips; title / View product open the PDP.
 * Parent calls setCarouselActive().
 */
if (!customElements.get('daddl-pack-tilt')) {
  customElements.define(
    'daddl-pack-tilt',
    class DaddlPackTilt extends HTMLElement {
      connectedCallback() {
        this.card = this.querySelector('[data-tilt-card]');
        this.flipper = this.querySelector('[data-tilt-flipper]');
        this.shadow = this.querySelector('[data-tilt-shadow]');
        this.hint = this.querySelector('[data-tilt-hint]');
        this.flipBtn = this.querySelector('[data-tilt-flip-btn]');
        if (!this.card || !this.flipper) return;

        this.amplitude = Number(this.getAttribute('data-rotate')) || 14;
        this.hoverScale = Number(this.getAttribute('data-scale')) || 1.05;
        this.embed = this.hasAttribute('data-embed');
        this.carouselActive = !this.embed;
        this.flipped = false;
        this.hovering = false;
        this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.reduce || this.embed) this.classList.remove('is-float');
        this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.lowMem = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 2;
        this.inView = false;
        this.pageHidden = document.visibilityState === 'hidden';
        this.ptr = { x: 0, y: 0, id: null };
        this.holding = false;
        this.dragging = false;
        this.rx = { value: 0, velocity: 0 };
        this.ry = { value: 0, velocity: 0 };
        this.sc = { value: 1, velocity: 0 };
        this.targetRx = 0;
        this.targetRy = 0;
        this.targetSc = 1;
        this.flipAngle = 0;
        this.flipLift = 0;
        this.flipAnim = null;
        this.pendingFace = null;
        this.raf = 0;
        this.running = false;
        this.lastT = 0;
        this.rect = null;
        this.rectDirty = true;
        this.lastCardTf = '';
        this.lastFlipTf = '';
        this.lastShadowTf = '';
        this.lastGx = 50;
        this.lastGy = 50;

        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFlipBtn = this.onFlipBtn.bind(this);
        this.onEmbedTap = this.onEmbedTap.bind(this);
        this.onIntersect = this.onIntersect.bind(this);
        this.onVisibility = this.onVisibility.bind(this);
        this.onRectDirty = this.onRectDirty.bind(this);
        this.tick = this.tick.bind(this);

        if (!this.embed) {
          this.card.addEventListener('pointerdown', this.onPointerDown);
          this.card.addEventListener('keydown', this.onKeyDown);
        }
        if (this.flipBtn) {
          this.flipBtn.addEventListener('click', this.onFlipBtn);
          this.flipBtn.addEventListener('pointerdown', this.onFlipBtn);
        }
        if (this.embed && !this.canHover) {
          this.card.addEventListener('click', this.onEmbedTap);
        }
        if (this.canHover && !this.reduce && !this.lowMem) {
          this.card.addEventListener('mouseenter', this.onMouseEnter);
          this.card.addEventListener('mouseleave', this.onMouseLeave);
          if (!this.embed) {
            this.card.addEventListener('mousemove', this.onMouseMove, { passive: true });
          }
        }

        this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0.08 });
        this.observer.observe(this);
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      disconnectedCallback() {
        this.card?.removeEventListener('pointerdown', this.onPointerDown);
        this.card?.removeEventListener('pointermove', this.onPointerMove);
        this.card?.removeEventListener('pointerup', this.onPointerUp);
        this.card?.removeEventListener('pointercancel', this.onPointerUp);
        this.card?.removeEventListener('keydown', this.onKeyDown);
        this.flipBtn?.removeEventListener('click', this.onFlipBtn);
        this.flipBtn?.removeEventListener('pointerdown', this.onFlipBtn);
        this.card?.removeEventListener('click', this.onEmbedTap);
        this.card?.removeEventListener('mousemove', this.onMouseMove);
        this.card?.removeEventListener('mouseenter', this.onMouseEnter);
        this.card?.removeEventListener('mouseleave', this.onMouseLeave);
        window.removeEventListener('scroll', this.onRectDirty, true);
        window.removeEventListener('resize', this.onRectDirty);
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.stopLoop();
        this.observer?.disconnect();
      }

      onVisibility() {
        this.pageHidden = document.visibilityState === 'hidden';
        if (this.pageHidden) this.pauseHard();
        else this.tryFloat();
      }

      onIntersect(entries) {
        this.inView = entries.some((entry) => entry.isIntersecting);
        if (!this.inView || this.pageHidden) {
          if (this.pageHidden || !this.flipAnim) {
            this.pauseHard();
            return;
          }
          this.targetRx = 0;
          this.targetRy = 0;
          this.targetSc = 1;
          this.kick();
          return;
        }
        this.tryFloat();
      }

      /* Homepage rail: only the centred slide floats / flips. */
      setCarouselActive(on) {
        this.carouselActive = !this.embed || !!on;
        if (!this.carouselActive) {
          this.resetToFront();
          this.classList.remove('is-live');
          this.classList.remove('is-float');
          this.stopLoop();
          this.clearCardTransform();
          return;
        }
        this.tryFloat();
      }

      resetToFront() {
        this.pendingFace = null;
        this.flipAnim = null;
        this.flipped = false;
        this.flipAngle = 0;
        this.flipLift = 0;
        this.classList.remove('is-flipping');
        this.writeFlipper();
        this.syncLabels();
      }

      /* CSS orbit on the bob. Keep the class while interacting so pause
         (not animation: none) can resume the same keyframe. */
      tryFloat() {
        const ok =
          !this.reduce &&
          this.inView &&
          !this.pageHidden &&
          this.carouselActive;
        this.classList.toggle('is-float', ok);
      }

      pauseHard() {
        if (this.flipAnim) {
          this.flipAngle = this.flipAnim.to;
          this.flipAnim = null;
          this.classList.remove('is-flipping');
          this.syncLabels();
        }
        this.rx.value = 0;
        this.rx.velocity = 0;
        this.ry.value = 0;
        this.ry.velocity = 0;
        this.sc.value = 1;
        this.sc.velocity = 0;
        this.targetRx = 0;
        this.targetRy = 0;
        this.targetSc = 1;
        this.flipLift = 0;
        this.clearCardTransform();
        this.writeFlipper();
        this.stopLoop();
        this.classList.remove('is-live');
        this.classList.remove('is-float');
      }

      clearCardTransform() {
        this.card.style.transform = '';
        this.lastCardTf = '';
        if (this.shadow) {
          this.shadow.style.transform = '';
          this.lastShadowTf = '';
        }
      }

      writeFlipper() {
        const flipTf = `rotateY(${this.flipAngle.toFixed(2)}deg)`;
        if (flipTf === this.lastFlipTf) return;
        this.lastFlipTf = flipTf;
        this.flipper.style.transform = flipTf;
      }

      onRectDirty() {
        this.rectDirty = true;
      }

      cardRect() {
        if (this.rectDirty || !this.rect) {
          this.rect = this.card.getBoundingClientRect();
          this.rectDirty = false;
        }
        return this.rect;
      }

      kick() {
        if (this.reduce || this.running) return;
        this.running = true;
        if (!this.embed) this.classList.add('is-live');
        this.lastT = 0;
        this.raf = requestAnimationFrame(this.tick);
      }

      stopLoop() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = 0;
        this.running = false;
      }

      onPointerDown(event) {
        if (event.button && event.button !== 0) return;
        this.holding = true;
        this.dragging = false;
        this.ptr.x = event.clientX;
        this.ptr.y = event.clientY;
        this.ptr.id = event.pointerId;
        this.rectDirty = true;
        this.card.addEventListener('pointerup', this.onPointerUp);
        this.card.addEventListener('pointercancel', this.onPointerUp);
        if (this.embed) return;
        this.card.setPointerCapture(event.pointerId);
        this.card.addEventListener('pointermove', this.onPointerMove, { passive: false });
      }

      onPointerMove(event) {
        if (!this.holding) return;
        const dx = event.clientX - this.ptr.x;
        const dy = event.clientY - this.ptr.y;
        if (!this.dragging && Math.hypot(dx, dy) > 8) {
          this.dragging = true;
          this.classList.add('is-dragging');
        }
        if (!this.dragging) return;
        event.preventDefault();
        this.aimFromPoint(event.clientX, event.clientY);
      }

      onPointerUp(event) {
        const wasDrag = this.dragging;
        const x = event.clientX;
        this.card.removeEventListener('pointermove', this.onPointerMove);
        this.card.removeEventListener('pointerup', this.onPointerUp);
        this.card.removeEventListener('pointercancel', this.onPointerUp);
        this.endHold();
        if (wasDrag) return;
        if (Math.hypot(x - this.ptr.x, event.clientY - this.ptr.y) > 10) return;
        this.startFlip(x);
      }

      endHold() {
        if (this.ptr.id != null && this.card.hasPointerCapture(this.ptr.id)) {
          this.card.releasePointerCapture(this.ptr.id);
        }
        this.holding = false;
        this.dragging = false;
        this.ptr.id = null;
        this.classList.remove('is-dragging');
        if (this.canHover && this.hovering) return;
        this.targetRx = 0;
        this.targetRy = 0;
        this.targetSc = 1;
        this.kick();
      }

      aimFromPoint(clientX, clientY) {
        const rect = this.cardRect();
        if (!rect.width || !rect.height) return;
        const nx = (clientX - rect.left) / rect.width - 0.5;
        const ny = (clientY - rect.top) / rect.height - 0.5;
        const mute = this.flipAnim ? 0.45 : 1;
        this.targetRx = ny * 2 * -this.amplitude * mute;
        this.targetRy = nx * 2 * this.amplitude * mute;
        this.targetSc = this.hoverScale;
        this.setGlare(nx * 2, ny * 2);
        this.kick();
      }

      onKeyDown(event) {
        if (this.embed) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        const rect = this.cardRect();
        this.startFlip(rect.left + rect.width * 0.65);
      }

      onFlipBtn(event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.type !== 'click') return;
        this.flipTo(!this.flipped);
      }

      /* Phone: the pack link must not open the PDP — tap flips. */
      onEmbedTap(event) {
        if (!this.carouselActive) return;
        event.preventDefault();
        event.stopPropagation();
        this.flipTo(!this.flipped);
      }

      /* Face the given side. Homepage hover uses this so click can stay a PDP link. */
      flipTo(wantBack) {
        if (this.embed && !this.carouselActive) return;
        if (this.flipAnim) {
          this.pendingFace = !!wantBack;
          return;
        }
        if (!!wantBack === this.flipped) return;
        const rect = this.cardRect();
        this.startFlip(wantBack ? rect.left + rect.width * 0.7 : rect.left + rect.width * 0.3);
      }

      onMouseEnter() {
        if (this.embed && !this.carouselActive) return;
        this.hovering = true;
        this.rectDirty = true;
        window.addEventListener('scroll', this.onRectDirty, { passive: true, capture: true });
        window.addEventListener('resize', this.onRectDirty, { passive: true });
        if (this.embed) {
          this.flipTo(true);
          return;
        }
        if (!this.flipAnim) this.targetSc = this.hoverScale;
        this.kick();
      }

      onMouseLeave() {
        this.hovering = false;
        window.removeEventListener('scroll', this.onRectDirty, true);
        window.removeEventListener('resize', this.onRectDirty);
        if (this.embed) {
          this.flipTo(false);
          return;
        }
        this.targetRx = 0;
        this.targetRy = 0;
        if (!this.flipAnim) this.targetSc = 1;
        this.kick();
      }

      onMouseMove(event) {
        if (this.holding) return;
        this.aimFromPoint(event.clientX, event.clientY);
      }

      startFlip(clientX) {
        if (this.flipAnim) return;
        if (this.embed && !this.carouselActive) return;
        const rect = this.cardRect();
        const dir = clientX >= rect.left + rect.width / 2 ? 1 : -1;
        this.flipped = !this.flipped;
        this.classList.add('is-flipping');
        if (this.card.hasAttribute('aria-pressed')) {
          this.card.setAttribute('aria-pressed', this.flipped ? 'true' : 'false');
        }
        this.flipAnim = {
          start: 0,
          dur: this.reduce ? 1 : 680,
          from: this.flipAngle,
          to: this.flipAngle + 180 * dir,
          swapped: false,
        };
        if (this.hint) this.hint.classList.add('is-dim');
        this.kick();
      }

      syncLabels() {
        const next = this.flipped
          ? this.getAttribute('data-label-front')
          : this.getAttribute('data-label-back');
        if (this.embed) {
          if (this.flipBtn && next) this.flipBtn.setAttribute('aria-label', next);
          return;
        }
        if (next) this.card.setAttribute('aria-label', next);
        if (this.hint) {
          const copy = this.flipped
            ? this.getAttribute('data-hint-front')
            : this.getAttribute('data-hint-back');
          if (copy) this.hint.textContent = copy;
          this.hint.classList.remove('is-dim');
        }
      }

      setGlare(nx, ny) {
        const x = 50 + nx * 28;
        const y = 50 + ny * 28;
        if (Math.abs(x - this.lastGx) < 0.6 && Math.abs(y - this.lastGy) < 0.6) return;
        this.lastGx = x;
        this.lastGy = y;
        this.style.setProperty('--tilt-gx', x.toFixed(1) + '%');
        this.style.setProperty('--tilt-gy', y.toFixed(1) + '%');
      }

      tick(now) {
        if (this.pageHidden) {
          this.pauseHard();
          return;
        }
        if (!this.inView && !this.flipAnim) {
          this.pauseHard();
          return;
        }

        const busy = this.applyFrame(now);
        if (busy || this.holding || this.flipAnim || (!this.embed && this.hovering)) {
          this.raf = requestAnimationFrame(this.tick);
          return;
        }
        this.stopLoop();
        this.classList.remove('is-live');
        if (!this.embed) this.clearCardTransform();
        this.tryFloat();
      }

      applyFrame(now) {
        const dt = Math.min(0.032, this.lastT ? (now - this.lastT) / 1000 : 0.016);
        this.lastT = now;
        this.stepFlip(now);
        this.writeFlipper();
        if (this.embed) return !!this.flipAnim;

        const sx = this.step(this.rx, this.targetRx, dt);
        const sy = this.step(this.ry, this.targetRy, dt);
        const ss = this.step(this.sc, this.targetSc, dt);

        const lift = this.flipLift;
        const scale = this.sc.value * (1 + lift * 0.07);
        const rise = (-12 * lift).toFixed(2);
        const cardTf = `translateY(${rise}px) rotateX(${this.rx.value.toFixed(2)}deg) rotateY(${this.ry.value.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        if (cardTf !== this.lastCardTf) {
          this.lastCardTf = cardTf;
          this.card.style.transform = cardTf;
        }
        if (this.shadow) {
          const shadowTf = `translate(${(this.ry.value * 0.35).toFixed(1)}px, ${(6 + this.rx.value * 0.2).toFixed(1)}px) scale(${(1.02 + lift * 0.08).toFixed(2)}, 1)`;
          if (shadowTf !== this.lastShadowTf) {
            this.lastShadowTf = shadowTf;
            this.shadow.style.transform = shadowTf;
          }
        }
        return !sx || !sy || !ss || !!this.flipAnim;
      }

      stepFlip(now) {
        const anim = this.flipAnim;
        if (!anim) {
          this.flipLift = 0;
          return;
        }
        if (!anim.start) anim.start = now;
        const p = Math.min(1, (now - anim.start) / anim.dur);
        const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        this.flipAngle = anim.from + (anim.to - anim.from) * e;
        this.flipLift = Math.sin(p * Math.PI);
        if (p >= 0.5 && !anim.swapped) {
          anim.swapped = true;
          this.syncLabels();
        }
        if (p >= 1) {
          this.flipAngle = anim.to;
          this.flipLift = 0;
          this.flipAnim = null;
          this.classList.remove('is-flipping');
          this.targetSc = this.hovering ? this.hoverScale : 1;
          if (this.pendingFace != null && this.pendingFace !== this.flipped) {
            const next = this.pendingFace;
            this.pendingFace = null;
            this.flipTo(next);
          } else {
            this.pendingFace = null;
          }
        }
      }

      step(state, target, dt) {
        const err = state.value - target;
        if (Math.abs(err) < 0.02 && Math.abs(state.velocity) < 0.04) {
          state.value = target;
          state.velocity = 0;
          return true;
        }
        const force = -100 * err - 30 * state.velocity;
        state.velocity += (force / 2) * dt;
        state.value += state.velocity * dt;
        return false;
      }
    }
  );
}
